/**
 * Tiny fetch wrapper used by feature/* slices to talk to Laravel without
 * spinning up axios. The CSRF token comes from the XSRF-TOKEN cookie, which
 * Laravel re-issues on every response, so it never goes stale during an SPA
 * session — unlike the one-time <meta name="csrf-token"> tag rendered by Blade.
 */

import { toast } from 'sonner';

import { isReadOnlyDenial, openReadOnlyDialog } from './readOnly';
import { isSecondFactorChallenge, requestSecondFactorConfirmation, SECOND_FACTOR_REQUIRED_STATUS } from './secondFactor';

export function csrfToken(): string {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
}

export function jsonHeaders(withJsonBody = false): HeadersInit {
    return {
        Accept: 'application/json',
        'X-XSRF-TOKEN': csrfToken(),
        ...(withJsonBody ? { 'Content-Type': 'application/json' } : {}),
    };
}

/**
 * Recover from an expired session / rotated CSRF token (HTTP 419). A hard reload
 * pulls a fresh session cookie + XSRF-TOKEN cookie; if the session is gone, the
 * auth middleware redirects to login. Guards against an infinite reload loop: if
 * a 419 recurs within a few seconds of the last auto-reload, we stop reloading
 * and ask the user to sign in again instead.
 */
export function onSessionExpired(): void {
    const RELOAD_KEY = 'csrf_reload_at';
    const RELOAD_WINDOW_MS = 10_000;
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');

    if (Date.now() - lastReloadAt < RELOAD_WINDOW_MS) {
        // Reloaded moments ago and still 419 — don't loop; prompt re-auth.
        toast.error('Сесія завершена. Будь ласка, увійдіть знову.');
        return;
    }

    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    toast.error('Сесія застаріла. Оновлюємо сторінку…');
    window.location.reload();
}

/**
 * The session itself is gone (401), not merely the CSRF token (419). Reloading
 * would only bounce through the auth middleware, so navigate straight to the
 * login page the server named. The server already recorded url.intended from
 * the Referer, so redirect()->intended() returns the user to this page once
 * they sign in. Guarded like onSessionExpired so a burst of failed polls
 * navigates once rather than fighting over window.location.
 */
export function onUnauthenticated(loginUrl = '/login'): void {
    const NAVIGATE_KEY = 'auth_redirect_at';
    const NAVIGATE_WINDOW_MS = 10_000;
    const lastNavigateAt = Number(sessionStorage.getItem(NAVIGATE_KEY) ?? '0');

    if (Date.now() - lastNavigateAt < NAVIGATE_WINDOW_MS) return;

    sessionStorage.setItem(NAVIGATE_KEY, String(Date.now()));
    toast.error('Сесія завершена. Будь ласка, увійдіть знову.');
    window.location.href = loginUrl;
}

export interface ApiOptions extends Omit<RequestInit, 'headers'> {
    params?: Record<string, string | number | boolean | undefined>;
    headers?: HeadersInit;
    /** Send a JSON body — auto-sets Content-Type and stringifies. */
    json?: unknown;
}

/** Thrown by `requestJson` when the server responds with a non-OK status. */
export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly payload: unknown,
    ) {
        super(`API request failed with status ${status}`);
        this.name = 'ApiError';
    }
}

function buildRequest(url: string, opts: ApiOptions): { target: string; init: RequestInit } {
    const { params, json, headers, ...rest } = opts;

    const search = new URLSearchParams();
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            if (v === undefined) continue;
            search.set(k, String(v));
        }
    }
    const target = search.size > 0 ? `${url}?${search.toString()}` : url;

    return {
        target,
        init: {
            credentials: 'same-origin',
            ...rest,
            headers: {
                ...jsonHeaders(json !== undefined),
                ...(headers ?? {}),
            },
            body: json !== undefined ? JSON.stringify(json) : (rest as RequestInit).body,
        },
    };
}

interface RawResult {
    status: number;
    ok: boolean;
    payload: unknown;
}

async function performRequest(url: string, opts: ApiOptions): Promise<RawResult> {
    const { target, init } = buildRequest(url, opts);
    const response = await fetch(target, init);

    if (response.status === 419) {
        onSessionExpired();
        throw new ApiError(419, null);
    }

    const text = await response.text();
    let payload: unknown = null;
    if (text) {
        try {
            payload = JSON.parse(text);
        } catch {
            payload = text;
        }
    }

    // Parsed before handling so the server-supplied login URL can be honoured —
    // UnauthenticatedResponse answers with { message, redirect }, the same shape
    // EnforceUserActions uses for its 403.
    if (response.status === 401) {
        const redirect = (payload as { redirect?: string } | null)?.redirect;
        onUnauthenticated(typeof redirect === 'string' ? redirect : undefined);
        throw new ApiError(401, payload);
    }

    return { status: response.status, ok: response.ok, payload };
}

/**
 * Like `apiFetch` but throws a typed {@link ApiError} (carrying the HTTP status
 * and parsed body) on failure, so callers can surface the server's message.
 * A 419 is auto-recovered (toast + reload) and a 401 navigates to login; both
 * still throw so callers stop.
 *
 * A 423 from the `passkey.confirm` middleware opens the in-app second factor
 * dialog (see ./secondFactor) and, once the user confirms, replays the request
 * exactly once. If the user cancels, or no dialog is mounted, the 423 is thrown
 * like any other failure.
 */
export async function requestJson<T = unknown>(url: string, opts: ApiOptions = {}): Promise<T> {
    let result = await performRequest(url, opts);

    if (isSecondFactorChallenge(result.status, result.payload)) {
        const confirmed = await requestSecondFactorConfirmation(result.payload);
        if (!confirmed) {
            throw new ApiError(result.status, result.payload);
        }
        result = await performRequest(url, opts);
    }

    if (!result.ok) {
        if (isReadOnlyDenial(result.status, result.payload)) {
            openReadOnlyDialog();
        }
        throw new ApiError(result.status, result.payload);
    }

    return result.payload as T;
}

/** Toast the best available message from a failed request (no-op for 419/401). */
export function surfaceApiError(error: unknown, fallback = 'Сталася помилка. Спробуйте ще раз.'): void {
    if (error instanceof ApiError) {
        if (error.status === 419) return; // already handled by onSessionExpired
        if (error.status === 401) return; // already handled by onUnauthenticated
        if (isReadOnlyDenial(error.status, error.payload)) return; // the read-only dialog is already open
        if (error.status === SECOND_FACTOR_REQUIRED_STATUS) {
            // The user closed the second factor dialog; the action was not performed.
            toast.error('Дію не виконано: потрібне підтвердження другого фактора.');
            return;
        }
        const payload = error.payload as { message?: string; errors?: Record<string, string[]> } | null;
        const firstFieldError = payload?.errors ? Object.values(payload.errors)[0]?.[0] : undefined;
        toast.error(payload?.message || firstFieldError || fallback);
        return;
    }
    toast.error(fallback);
}

/**
 * Backwards-compatible convenience wrapper: returns the parsed body, or `null`
 * on any failure (419, 401, non-OK, empty/invalid body). New code that needs to
 * react to errors should prefer {@link requestJson} + {@link surfaceApiError}.
 */
export async function apiFetch<T = unknown>(url: string, opts: ApiOptions = {}): Promise<T | null> {
    try {
        const result = await requestJson<T>(url, opts);
        return (result ?? null) as T | null;
    } catch {
        return null;
    }
}
